/**
 * Utility functions for calculating quantity discounts on frontend
 */

/**
 * Calculate the best price for a given quantity based on discount tiers
 * @param {number} quantity - Quantity of items
 * @param {number} basePrice - Base price per unit
 * @param {Array} quantityDiscounts - Array of discount tiers
 * @returns {Object} - Pricing information with discounts
 */
export const calculateBestPrice = (quantity, basePrice, quantityDiscounts = []) => {
  if (!quantity || quantity <= 0 || !basePrice) {
    return {
      unitPrice: basePrice || 0,
      totalPrice: (basePrice || 0) * (quantity || 0),
      discountPerUnit: 0,
      totalDiscount: 0,
      appliedTier: null
    };
  }

  if (!quantityDiscounts || quantityDiscounts.length === 0) {
    return {
      unitPrice: basePrice,
      totalPrice: basePrice * quantity,
      discountPerUnit: 0,
      totalDiscount: 0,
      appliedTier: null
    };
  }

  // Sort discounts by minQuantity (ascending)
  const sortedDiscounts = [...quantityDiscounts].sort((a, b) => a.minQuantity - b.minQuantity);
  
  // Find applicable discount tier
  let applicableDiscount = null;
  
  for (const discount of sortedDiscounts) {
    const minQty = discount.minQuantity;
    const maxQty = discount.maxQuantity;
    
    if (quantity >= minQty && (maxQty === null || maxQty === undefined || quantity <= maxQty)) {
      applicableDiscount = discount;
      // Don't break - continue to find the best applicable tier
    }
  }

  if (!applicableDiscount) {
    return {
      unitPrice: basePrice,
      totalPrice: basePrice * quantity,
      discountPerUnit: 0,
      totalDiscount: 0,
      appliedTier: null
    };
  }

  // Calculate discounted prices
  const discountedUnitPrice = applicableDiscount.pricePerUnit;
  const totalPrice = discountedUnitPrice * quantity;
  const discountPerUnit = basePrice - discountedUnitPrice;
  const totalDiscount = discountPerUnit * quantity;

  return {
    unitPrice: discountedUnitPrice,
    totalPrice: totalPrice,
    discountPerUnit: discountPerUnit,
    totalDiscount: totalDiscount,
    appliedTier: {
      minQuantity: applicableDiscount.minQuantity,
      maxQuantity: applicableDiscount.maxQuantity,
      pricePerUnit: applicableDiscount.pricePerUnit
    }
  };
};

/**
 * Get formatted discount tiers for display
 * @param {number} basePrice - Base price per unit
 * @param {Array} quantityDiscounts - Array of discount tiers
 * @returns {Array} - Formatted discount tiers for UI display
 */
export const getDiscountTiersDisplay = (basePrice, quantityDiscounts = []) => {
  if (!quantityDiscounts || quantityDiscounts.length === 0) {
    return [];
  }

  const sortedDiscounts = [...quantityDiscounts].sort((a, b) => a.minQuantity - b.minQuantity);
  const tiers = [];

  // Add base price tier (1 bottle)
  tiers.push({
    quantityRange: "1",
    unitPrice: basePrice,
    totalExample: basePrice,
    savingsPerUnit: 0,
    savingsPercentage: 0,
    isBase: true
  });

  // Add discount tiers
  sortedDiscounts.forEach(discount => {
    let quantityRange = discount.minQuantity.toString();
    if (discount.maxQuantity && discount.maxQuantity !== null) {
      quantityRange += `-${discount.maxQuantity}`;
    } else {
      quantityRange += "+";
    }

    const savingsPerUnit = basePrice - discount.pricePerUnit;
    const savingsPercentage = basePrice > 0 ? (savingsPerUnit / basePrice) * 100 : 0;

    tiers.push({
      quantityRange: quantityRange,
      unitPrice: discount.pricePerUnit,
      totalExample: discount.pricePerUnit * discount.minQuantity,
      savingsPerUnit: savingsPerUnit,
      savingsPercentage: savingsPercentage,
      isBase: false
    });
  });

  return tiers;
};

/**
 * Calculate cart totals with quantity discounts applied at CART LEVEL (improved logic)
 * @param {Array} cartItems - Array of cart items
 * @param {Array} globalQuantityDiscounts - Global quantity discount tiers
 * @returns {Object} - Cart totals with discount information
 */
export const calculateCartTotals = (cartItems = [], globalQuantityDiscounts = []) => {
  if (!cartItems || cartItems.length === 0) {
    return {
      subtotal: 0,
      totalDiscount: 0,
      finalTotal: 0,
      originalTotal: 0,
      itemsWithDiscounts: [],
      totalQuantity: 0,
      appliedDiscountTier: null
    };
  }

  let baseProductsSubtotal = 0;
  let extrasSubtotal = 0;
  let totalQuantity = 0;
  let itemsWithDetails = [];

  // First pass: Calculate totals for base products and separate extras
  cartItems.forEach(item => {
    const quantity = item.quantity || 0;
    const basePrice = item.productData?.basePrice || item.basePrice || item.price || 0;
    
    // Separate base product cost from extras
    const bottleCost = item.productData?.bottleCost || 0;
    const designCost = item.productData?.designCost || 0;
    
    const baseProductPrice = basePrice - bottleCost - designCost;
    const baseProductTotal = baseProductPrice * quantity;
    const extrasTotal = (bottleCost + designCost) * quantity;
    
    baseProductsSubtotal += baseProductTotal;
    extrasSubtotal += extrasTotal;
    totalQuantity += quantity;

    itemsWithDetails.push({
      ...item,
      baseProductPrice: baseProductPrice,
      baseProductTotal: baseProductTotal,
      bottleCost: bottleCost,
      designCost: designCost,
      extrasTotal: extrasTotal,
      originalItemTotal: basePrice * quantity
    });
  });

  // Apply cart-level quantity discount based on total quantity
  let appliedDiscountTier = null;
  let discountRate = 0;
  
  if (globalQuantityDiscounts && globalQuantityDiscounts.length > 0) {
    // Sort discounts by minQuantity (descending to get best applicable discount)
    const sortedDiscounts = [...globalQuantityDiscounts].sort((a, b) => b.minQuantity - a.minQuantity);
    
    for (const discount of sortedDiscounts) {
      if (totalQuantity >= discount.minQuantity && 
          (discount.maxQuantity === null || discount.maxQuantity === undefined || totalQuantity <= discount.maxQuantity)) {
        appliedDiscountTier = discount;
        // Convert percentage discount to rate
        discountRate = discount.discountPercentage / 100;
        break;
      }
    }
  }

  // Apply discount only to base products, not extras
  const discountAmount = baseProductsSubtotal * discountRate;
  const discountedBaseProductsSubtotal = baseProductsSubtotal - discountAmount;
  
  // Apply proportional discount to each item
  const itemsWithDiscounts = itemsWithDetails.map(item => {
    const itemDiscountProportion = item.baseProductTotal / baseProductsSubtotal;
    const itemDiscount = discountAmount * itemDiscountProportion;
    const discountedBaseProductTotal = item.baseProductTotal - itemDiscount;
    const finalItemTotal = discountedBaseProductTotal + item.extrasTotal;
    
    return {
      ...item,
      itemDiscount: itemDiscount,
      discountedBaseProductTotal: discountedBaseProductTotal,
      finalItemTotal: finalItemTotal,
      hasDiscount: itemDiscount > 0
    };
  });

  const finalTotal = discountedBaseProductsSubtotal + extrasSubtotal;
  const originalTotal = baseProductsSubtotal + extrasSubtotal;

  return {
    subtotal: finalTotal,
    baseProductsSubtotal: baseProductsSubtotal,
    extrasSubtotal: extrasSubtotal,
    totalDiscount: discountAmount,
    finalTotal: finalTotal,
    originalTotal: originalTotal,
    itemsWithDiscounts: itemsWithDiscounts,
    totalQuantity: totalQuantity,
    appliedDiscountTier: appliedDiscountTier
  };
};

/**
 * Format price for display
 * @param {number} price - Price to format
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(price || 0);
};

/**
 * LEGACY: Calculate cart totals with per-item quantity discounts (old logic)
 * @param {Array} cartItems - Array of cart items
 * @returns {Object} - Cart totals with discount information
 */
export const calculateCartTotalsLegacy = (cartItems = []) => {
  let subtotal = 0;
  let totalDiscount = 0;
  let itemsWithDiscounts = [];

  cartItems.forEach(item => {
    const quantity = item.quantity || 0;
    const basePrice = item.productData?.basePrice || item.basePrice || 0;
    const quantityDiscounts = item.productData?.quantityDiscounts || [];

    const pricing = calculateBestPrice(quantity, basePrice, quantityDiscounts);
    
    subtotal += pricing.totalPrice;
    totalDiscount += pricing.totalDiscount;

    itemsWithDiscounts.push({
      ...item,
      pricing: pricing,
      hasDiscount: pricing.totalDiscount > 0
    });
  });

  return {
    subtotal: subtotal,
    totalDiscount: totalDiscount,
    finalTotal: subtotal, // Subtotal already includes discounts
    originalTotal: subtotal + totalDiscount, // What it would cost without discounts
    itemsWithDiscounts: itemsWithDiscounts
  };
};

/**
 * Get global quantity discounts from backend API
 * @returns {Promise<Array>} - Array of global quantity discount tiers
 */
export const getGlobalQuantityDiscounts = async () => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${backendUrl}/api/settings/quantity-discounts`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch discount settings');
    }
    
    const data = await response.json();
    if (data.success && data.discounts) {
      // Filter only active discounts and return them
      return data.discounts.filter(discount => discount.isActive);
    }
    
    // Fallback to default discounts if API fails or returns empty
    return getDefaultQuantityDiscounts();
  } catch (error) {
    console.warn('Failed to fetch global quantity discounts from API:', error);
    // Fallback to default discounts
    return getDefaultQuantityDiscounts();
  }
};

/**
 * Get default quantity discounts (fallback)
 * @returns {Array} - Array of default quantity discount tiers
 */
export const getDefaultQuantityDiscounts = () => {
  return [
    { minQuantity: 3, maxQuantity: 5, discountPercentage: 5, isActive: true },
    { minQuantity: 6, maxQuantity: 11, discountPercentage: 10, isActive: true },
    { minQuantity: 12, maxQuantity: null, discountPercentage: 15, isActive: true }
  ];
};

/**
 * Get next discount tier to incentivize more purchases (backward compatible)
 * @param {number} currentQuantity - Current total quantity in cart
 * @param {number|Array} basePrice - Base price (legacy) or discount array (new)
 * @param {Array} quantityDiscounts - Array of discount tiers (optional for backward compatibility)
 * @returns {Object|null} - Next available discount tier or null
 */
export const getNextDiscountTier = (currentQuantity, basePrice = null, quantityDiscounts = null) => {
  // Handle backward compatibility: if second param is array, it's the new signature
  let discounts = [];
  
  if (Array.isArray(basePrice)) {
    // New signature: getNextDiscountTier(quantity, discounts)
    discounts = basePrice;
  } else if (Array.isArray(quantityDiscounts)) {
    // Legacy signature: getNextDiscountTier(quantity, basePrice, discounts)
    discounts = quantityDiscounts;
  } else {
    // No valid discounts provided
    return null;
  }

  // Ensure discounts is iterable and not empty
  if (!discounts || !Array.isArray(discounts) || discounts.length === 0) {
    return null;
  }

  // Additional safety check - ensure all discount objects have required properties
  const validDiscounts = discounts.filter(discount => 
    discount && 
    typeof discount === 'object' && 
    typeof discount.minQuantity === 'number'
  );

  if (validDiscounts.length === 0) {
    return null;
  }

  const sortedDiscounts = [...validDiscounts].sort((a, b) => a.minQuantity - b.minQuantity);
  
  for (const discount of sortedDiscounts) {
    if (currentQuantity < discount.minQuantity) {
      const additionalNeeded = discount.minQuantity - currentQuantity;
      
      // Handle both old and new discount format
      if (discount.discountPercentage !== undefined) {
        // New format with percentage
        return {
          minQuantity: discount.minQuantity,
          additionalNeeded: additionalNeeded,
          discountPercentage: discount.discountPercentage,
          totalQuantityNeeded: discount.minQuantity
        };
      } else if (discount.pricePerUnit !== undefined && basePrice && typeof basePrice === 'number') {
        // Legacy format with pricePerUnit
        const savingsPerUnit = basePrice - discount.pricePerUnit;
        return {
          minQuantity: discount.minQuantity,
          additionalNeeded: additionalNeeded,
          newUnitPrice: discount.pricePerUnit,
          savingsPerUnit: savingsPerUnit,
          totalSavingsIfUpgrade: savingsPerUnit * discount.minQuantity
        };
      }
    }
  }
  
  return null;
};